import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  Calendar, 
  MessageCircle, 
  Palette, 
  Mountain, 
  Store, 
  Info, 
  LogOut,
  Activity,
  ChevronLeft,
  ChevronRight,
  Puzzle,
  Gift,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { PROJECT_CREATOR } from '../constants/contacts';

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar = ({ isCollapsed, onToggle }: SidebarProps) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home },
    { name: 'Esperienze', href: '/esperienze', icon: Activity },
    { name: 'Eventi', href: '/eventi', icon: Calendar },
    { name: 'Prompt & Genius Loci', href: '/prompt', icon: MessageCircle },
    { name: 'Quiz', href: '/quiz', icon: Puzzle },
    { name: 'Opere', href: '/opere', icon: Palette },
    { name: 'Sentieri', href: '/sentieri', icon: Mountain },
    { name: 'Produttori', href: '/produttori', icon: Store },
    { name: 'Attività Locali', href: '/attivita-locali', icon: Store },
    { name: 'Souvenir Digitali', href: '/souvenir', icon: Gift },
    { name: 'Info', href: '/chi-siamo', icon: Info },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div className={`bg-white shadow-lg border-r border-border transition-all duration-300 fixed left-0 top-0 h-full z-50 ${
      isCollapsed ? 'w-16' : 'w-64'
    }`}>
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          {!isCollapsed && (
            <Link to="/landing" className="flex items-center space-x-3">
              <img 
                src="/t-alento_logo.png" 
                alt="T-Alento" 
                className="h-10 w-auto object-contain"
              />
            </Link>
          )}
          {isCollapsed && (
            <Link to="/landing" className="flex justify-center">
              <img 
                src="/t-alento_logo.png" 
                alt="T-Alento" 
                className="h-8 w-8 object-contain"
              />
            </Link>
          )}
          <button
            onClick={onToggle}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-2">
        {navigation.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex items-center space-x-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                isActive(item.href)
                  ? 'bg-primary text-white'
                  : 'text-text-secondary hover:text-primary hover:bg-primary/5'
              }`}
              title={isCollapsed ? item.name : undefined}
            >
              <Icon size={20} />
              {!isCollapsed && <span>{item.name}</span>}
            </Link>
          );
        })}
      </nav>

      {/* User Info & Logout */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border bg-white">
        {!isCollapsed ? (
          <div className="space-y-3">
            {/* Made with love message */}
            <div className="text-xs text-center text-text-secondary/60 pb-2 border-b border-border/50">
              <p>made with ❤️ in Benevento by</p>
              <a 
                href={PROJECT_CREATOR.website}
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:text-secondary transition-colors duration-200"
              >
                {PROJECT_CREATOR.name}
              </a>
            </div>
            <div className="text-sm">
              <p className="font-medium text-text-primary">{user?.email}</p>
              <p className="text-text-secondary">Amministratore</p>
            </div>
            <button
              onClick={logout}
              className="flex items-center space-x-3 w-full px-3 py-2 text-text-secondary hover:text-error hover:bg-error/5 rounded-lg transition-colors duration-200"
            >
              <LogOut size={20} />
              <span>Esci</span>
            </button>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-3">
            <button
              onClick={logout}
              className="p-2 text-text-secondary hover:text-error hover:bg-error/5 rounded-lg transition-colors duration-200"
              title="Esci"
            >
              <LogOut size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;
